/**
 * Order Log — API row → view row
 * ─────────────────────────────────────────────────────────────────────────────
 * Normalises one `/sales/live/v2/order-log` order into the shape the table and
 * the mobile cards render. Every value comes from the response. A field the
 * endpoint does not return stays `null` and renders as an em dash, which is
 * deliberately distinct from a genuine zero (ORD-1.4).
 */

import type { OrderLogItem } from '../../../../interface/clickHouseInterface';
import type { OrderLogRow, OrderLogFinancials } from './orderLogModel';
import type { RefundType } from '../../refunds/refundModel';

/** REF-5 — the API sends 'Full' | 'Item' | 'Fee'; `RefundType` is lowercase. */
const REFUND_TYPE_MAP: Record<string, RefundType> = { full: 'full', item: 'item', fee: 'fee' };

/** The API sends an em dash for "no value" in its string fields. */
const EM_DASH_VALUES = ['—', '–', '-', ''];
const str = (v: any): string | null => {
    if (v === null || v === undefined) return null;
    const t = String(v).trim();
    return EM_DASH_VALUES.indexOf(t) >= 0 ? null : t;
};

/** null = the field is unavailable; 0 = a genuine zero. The two never merge. */
const num = (v: any): number | null => {
    if (v === null || v === undefined || v === '') return null;
    const n = Number(v);
    return isNaN(n) ? null : n;
};

export const isThirdPartyChannel = (orderType: any): boolean =>
    /doordash|ubereats|uber\s?eats|grubhub|postmates|swiggy|zomato|deliveroo|3rd|third.?party|aggregator/i.test(String(orderType ?? ''));
/** Kiosk / online / aggregator orders have no employee — they show "System". */
const isSystemOriginated = (orderType: any): boolean =>
    isThirdPartyChannel(orderType) || /kiosk|online|web|self.?order|qr/i.test(String(orderType ?? ''));

/**
 * `tableName` arrives as an array of every table the check was closed on.
 * A merged check collapses to "R4 +1"; the full list stays on the row for the
 * tooltip, the slide-over and the export (ORD-1.2).
 */
function readTables(raw: any): { display: string | null; list: string[] } {
    const list = (Array.isArray(raw) ? raw : raw ? [raw] : [])
        .map((t: any) => str(t))
        .filter((t): t is string => !!t);
    if (!list.length) return { display: null, list: [] };
    return { display: list.length > 1 ? `${list[0]} +${list.length - 1}` : list[0], list };
}

function readFinancials(o: any): OrderLogFinancials {
    return {
        itemTotal: num(o?.itemTotal),
        // `totalDiscount` covers order- and item-level offers; `discount` is the
        // older field, used only until every response carries the new one.
        discount: num(o?.totalDiscount ?? o?.discount),
        serviceCharge: num(o?.serviceCharge),
        deliveryFee: num(o?.deliveryFee),
        // `otherCharge` is the field's internal name; it holds the API's
        // convenienceFee and is labelled "Convenience Fee" in the slide-over.
        otherCharge: num(o?.convenienceFee),
        tax: num(o?.tax),
        tips: num(o?.tips),
        gratuity: num(o?.gratuity),
        processingFee: num(o?.processingFee),
        grandTotal: num(o?.amount) ?? 0,
        // Not returned by the order-log list endpoint today — an em dash, never a
        // fabricated 0. Populated on the order-detail path (financialsFromDetail).
        refundAmount: num(o?.refundAmount),
    };
}

export function toOrderLogRow(o: OrderLogItem | any, index: number): OrderLogRow {
    const orderNo = str(o?.orderNo) ?? str(o?.orderId) ?? '—';
    const orderType = str(o?.orderType) ?? '—';
    const tables = readTables(o?.tableName);

    let staffName = str(o?.staffName ?? o?.serverName);
    if (!staffName && isSystemOriginated(orderType)) staffName = 'System';

    const financials = readFinancials(o);

    // REF-5 — real refund fields off the order-log row itself: `refundType` ('Full' |
    // 'Item' | 'Fee', absent when unrefunded) and `refunded` (total cash returned).
    const refundTypeKey = str(o?.refundType)?.toLowerCase();
    const refundType: RefundType | null = refundTypeKey ? (REFUND_TYPE_MAP[refundTypeKey] ?? null) : null;
    const refunded = num(o?.refunded);

    return {
        key: `${orderNo}-${index}`,
        raw: o,
        orderNo,
        orderId: String(o?.orderId ?? orderNo),
        orderDate: str(o?.orderDate),
        orderTime: str(o?.orderTime),
        orderType,
        customerName: str(o?.customerName),
        customerPhone: str(o?.customerPhone ?? o?.phone),
        tableName: tables.display,
        tableList: tables.list,
        guests: num(o?.guests ?? o?.guestCount),
        staffName,
        itemPreview: str(o?.itemPreview),
        cancelReason: str(o?.reason ?? o?.cancelReason),
        isThirdParty: isThirdPartyChannel(orderType),
        hasRefund: refunded != null && refunded > 0,
        refundType,
        refunded,
        financials,
    };
}

export function toOrderLogRows(orders: any[], _opts?: unknown): OrderLogRow[] {
    return (orders ?? []).map((o, i) => toOrderLogRow(o, i));
}
